import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import { DeletePathologyTest, GetPathologyTests } from "../../../../api/diagnosis/master/pathologyTestAPI";

const ListPathologyTest = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchPathologyTests = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetPathologyTests();
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          name: item.name || "",
          shortName: item.shortName || "",
          testType: item.testType || "",
          unit: item.unit || "",
          price: item.price ?? "",
          intlPrice: item.intlPrice ?? "",
          tat: item.tat ?? "",
          reportDays: item.reportDays ?? "",
          printSrNo: item.printSrNo ?? "",
          active: item.active,
          method: item.method || "",
          colHeadingHide: item.colHeadingHide,
          subCategory: item.subCategory,
          rateEditable: item.rateEditable,
          discountAllow: item.discountAllow
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error?.message || "Failed to load data");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchPathologyTests();
  }, []);

  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  const handleEditClick = id => {
    navigate(
      `/diagnosis/master/pathology-test/edit/${id}`
    );
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This test will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeletePathologyTest(id);
          if (response.success) {
            showToast("success", response.message);
            setData(data.filter(item => item.id !== id));
            setFilteredData(filteredData.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error?.message || "Delete failed");
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  /**
   * DataTable Columns
   */
  const columns = [
    { name: "S.No", selector: row => row.index, width: "10%" },
    { name: "Name", selector: row => row.name, width: "10%" },
    { name: "Short Name", selector: row => row.shortName, width: "10%" },
    { name: "Test Type", selector: row => row.testType, width: "10%" },
    { name: "Unit", selector: row => row.unit, width: "10%" },
    { name: "Price", selector: row => row.price, width: "10%" },
    { name: "Intl Price", selector: row => row.intlPrice, width: "10%" },
    { name: "TAT", selector: row => row.tat, width: "10%" },
    { name: "Report Days", selector: row => row.reportDays, width: "10%" },
    { name: "Print Sr No", selector: row => row.printSrNo, width: "10%" },
    { name: "Active", selector: row => row.active, width: "10%" },
    { name: "Method", selector: row => row.method, width: "10%" },
    { name: "Col Heading Hide", selector: row => row.colHeadingHide, width: "10%" },
    { name: "Sub Category", selector: row => row.subCategory, width: "10%" },
    { name: "Rate Editable", selector: row => row.rateEditable, width: "10%" },
    { name: "Discount Allow", selector: row => row.discountAllow, width: "10%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true, 
      width: "15%"
    }
  ];

  /**
   * CSV Export Headers (ALL fields)
   */
  const headers = [
    { label: "S.No", key: "index" },
    { label: "Name", key: "name" },
    { label: "Short Name", key: "shortName" },
    { label: "Test Type", key: "testType" },
    { label: "Unit", key: "unit" },
    { label: "Price", key: "price" },
    { label: "Intl Price", key: "intlPrice" },
    { label: "TAT", key: "tat" },
    { label: "Report Days", key: "reportDays" },
    { label: "Print Sr No", key: "printSrNo" },
    { label: "Method", key: "method" },
    { label: "Active", key: "active" },
    { label: "Col Heading Hide", key: "colHeadingHide" },
    { label: "Sub Category", key: "subCategory" },
    { label: "Rate Editable", key: "rateEditable" },
    { label: "Discount Allow", key: "discountAllow" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink
      data={data}
      headers={headers}
      filename="pathology-tests.csv"
    >
      <button className="btn btn-primary" id="export">
        Export
      </button>
      <UncontrolledTooltip target="export">
        Export to CSV
      </UncontrolledTooltip>
    </CSVLink>
  );

  return (
    <Fragment>
      <Breadcrumb parent="Pathology Test" title="List Pathology Tests" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>List Pathology Tests</h5>
              <Link
                to={`/diagnosis/master/pathology-test/add`}
              >
                <button className="btn btn-primary" id="create">
                  Create Pathology Test
                </button>
                <UncontrolledTooltip target="create">
                  Create Pathology Test
                </UncontrolledTooltip>
              </Link>
            </div>

            <div className="row mt-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search Pathology Test..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListPathologyTest;
